import { Fragment, useState } from 'react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Location, useLocation } from 'react-router-dom';

import NavItem from 'components/UI/NavItem/NavItem';

import { ACCOUNT_TYPES, DIVIDER, ENGATI_ROLES } from 'shared/consts/consts';
import { IObjProps } from 'shared/consts/types';
import { MARKETING_CAMPAIGNS_ENTITLEMENT_VALUES } from 'shared/enum';
import useLocationEffect from 'shared/hooks/common/useLocationEffect';

import { customDashboardNameSelector } from 'store/Analytics/selectors';
import { engagementTriggersEntilement, isWooCommerceEnabledSelector } from 'store/App/Shopify/selectors';
import { isAcceleratorEcomSelector } from 'store/App/User';
import {
	isAIAgentEntitlementEnabledSelector,
	isCDPEntitlementEnabledSelector,
	isDialog360ConfiguredAsProvider,
	isEmailTemplatesEnabledSelector,
	isShopifyAcceleratorEnabled,
	isShopifyEnabledAcc,
	isShopifySegmentsEnabled,
	isWhatsAppCloudConfiguredAsProvider,
	isWhatsAppConfiguredSelector,
	isWhatsAppPaymentEnabledSelector,
	marketingCampaignsAnalyticsSelector,
} from 'store/App/User/selectors';
import { isLLMEntitlementEnabled } from 'store/Bot/selectors';
import { isRcsConfiguredSelector } from 'store/Contacts/selectors';
import { RootState } from 'store/rootReducer';
import { brandingPlatformNameSelector } from 'store/Users/selectors';

import { ThreeColumnRouteLinks } from 'router/links/ThreeColumnRouteLinks';
import { LEFT_PANEL_ACCESS_ROUTES } from 'router/ProtectedRouter/protectedRoute';

import AccordionNavGroup, { type IAccordionNavSection, isAccordionNavSection } from './AccordionNavGroup';
import AdvancedHorizontalDivider from './AdvancedHorizontalDivider';

enum AnalyticsRoutekeys {
	PURCHASE_FUNNEL = 'Purchase Funnel',
	PRODUCT_ANALYTICS = 'Product Analytics',
	AGENT_CONVERSIONS = 'Agent Conversions',
}

const ShopifyNavigation = (props: { page: string }) => {
	const { page } = props;
	const location = useLocation();
	const [css, theme]: any = useStyletron();
	const { t } = useTranslation(['common']);
	const [pathInUrl, setCurrentPath] = useState(location.pathname);
	const marketingCampaignsAnalyticsType = useSelector(marketingCampaignsAnalyticsSelector);
	const isDialog360Configured = useSelector(isDialog360ConfiguredAsProvider);
	const isAgent = useSelector(
		(state: RootState) => state.User.profile.data.roleName === ENGATI_ROLES.ROLE_CONNECT_AGENT
	);
	const isSystemSegmentsEnabled = useSelector(isShopifySegmentsEnabled);
	const shopifyProductDiscoveryEntitlement = useSelector(
		(state: RootState) => state.User.botAdmin.data.enable_shopify_product_discovery
	);
	const isEcomAnalyticsConversionEnabled: boolean = useSelector(
		(state: RootState) => state.User.botAdmin.data.enable_ecom_conversion_for_analytics
	);
	const engagementTriggersEnabled = useSelector(engagementTriggersEntilement);
	const brandName: string = useSelector(brandingPlatformNameSelector);
	const leftPanelsV2Data: any = useSelector((state: RootState) => state.User.botAdmin.data.left_panels_v2_keys || []);
	const leftPanelsV2: any = useSelector((state: RootState) => state.User.botAdmin.data.left_panels_v2);
	const isWhatsAppCloudConfigured = useSelector(isWhatsAppCloudConfiguredAsProvider);
	const isWoocommerceEnabled = useSelector(isWooCommerceEnabledSelector);
	const isLLMEnabled: boolean = useSelector(isLLMEntitlementEnabled);
	const customDashboardName = useSelector(customDashboardNameSelector);
	const isWhatsAppConfigured = useSelector(isWhatsAppConfiguredSelector);
	const isWaPaymentEnabled = useSelector(isWhatsAppPaymentEnabledSelector);
	const isAcceleratorEcom = useSelector(isAcceleratorEcomSelector);
	const isAdminMode = useSelector((state: RootState) => state.User.botAdmin.data.is_admin_mode);
	const isEmailTemplatesEnabled = useSelector(isEmailTemplatesEnabledSelector);
	const isShopifyEnabled = useSelector(isShopifyEnabledAcc);
	const isCDPEntitlementEnabled = useSelector(isCDPEntitlementEnabledSelector);
	const isAIAgentEntitlementEnabled = useSelector(isAIAgentEntitlementEnabledSelector);
	const isShopifyAccelerator = useSelector(isShopifyAcceleratorEnabled);
	const isRcsConfigured = useSelector(isRcsConfiguredSelector);
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ engagement: true });

	const accountType = localStorage.getItem('loggedInUserAccountType');
	const isMasterAdmin = accountType === ACCOUNT_TYPES.MASTER_ADMIN;
	const isResellerAccount = accountType === ACCOUNT_TYPES.RESELLER;

	const {
		purchaseFunnelLink,
		productAnalyticsLink,
		exportHistoryLink,
		storeSetupLink,
		storeCustomersLink,
		storeProductsLink,
		storeConversionTriggersLink,
		broadcastEmailTemplates,
		agentLogsLink,
	} = ThreeColumnRouteLinks;

	const analyticsEngagementSection: IAccordionNavSection = {
		id: 'engagement',
		title: t('common:locations.navigation.engagement'),
		items: {
			...(isCDPEntitlementEnabled && isAIAgentEntitlementEnabled
				? {
						[t('common:locations.navigation.agentAnalytics')]: '/analytics/engagement/agent-analytics',
					}
				: {}),
			...(isCDPEntitlementEnabled
				? {
						[t('common:locations.navigation.qualificationAnalytics')]:
							'/analytics/engagement/qualification-analytics',
					}
				: {}),
			[t('common:locations.navigation.botEngagement')]: '/analytics/bot/engagement',
			[t('common:locations.navigation.botResponses')]: '/analytics/bot/response',
			[t('common:locations.navigation.userRetentionAnalysis')]: '/analytics/user/retention',
			[t('common:locations.navigation.automationEfficiency')]: '/analytics/livechat/automation-efficiency',
			[t('common:locations.navigation.marketingCampaigns')]: '/analytics/engagement/marketing-campaigns',
			[t('common:locations.navigation.platformInteractions')]: '/analytics/usage/platform-interactions',
			[t('common:locations.navigation.buttonClickAnalysis')]: '/analytics/usage/button-click-analysis',
			[t('common:locations.navigation.genAIQueryAnalysis')]: '/analytics/gen-ai/query-analysis',
		},
	};

	const analyticsSupportSection: IAccordionNavSection = {
		id: 'support',
		title: t('common:locations.navigation.support'),
		items: {
			'Live Chat Summary': '/analytics/livechat/summary',
			Agents: '/analytics/livechat/agents',
			Channels: '/analytics/bot/channels',
			'Agent Performance': '/analytics/livechat/agent-performance',
			[t('common:locations.navigation.liveChatDetails')]: '/analytics/livechat/details',
			[t('common:locations.navigation.feedbackSummary')]: '/analytics/livechat/feedback',
			[t('common:locations.navigation.slaBreaches')]: '/analytics/livechat/sla-breaches',
		},
	};

	const NavOptions: IObjProps = {
		train: [
			{
				Overview: '/train/overview',
				FAQs: '/train/faq',
				Documents: '/train/cognitive-search',
				'Smart Response Setup': '/train/settings',
			},
			{
				'Tabular Input': '/train/tabular-input',
				Entities: '/train/entities',
				'Response Training': '/train/responses',
			},
		],
		configure: [
			{
				Overview: '/configure/overview',
				'Bot Configuration': '/configure/bot-details',
				'Web Display': '/configure/display',
				'External Live Chat': '/configure/external-livechat',
				'Live Chat': '/configure/live-chat',
				Integrations: '/configure/integrations',
				'Channel Deployment': '/configure/deployment',
			},
			{
				Attributes: '/configure/attributes',
				Languages: '/configure/languages',
				'Voice Integrations': '/configure/advanced/voice',
				Formatting: '/configure/advanced/formatting',
				Security: '/configure/advanced/security',
			},
		],

		analytics: [
			{
				Overview: '/analytics/overview',
				...(isCDPEntitlementEnabled && customDashboardName
					? {
							[customDashboardName]: '/analytics/custom-dashboard',
						}
					: {}),
				...(isCDPEntitlementEnabled || !isShopifyAccelerator
					? {
							engagementAccordion: analyticsEngagementSection,
						}
					: {}),
				supportAccordion: analyticsSupportSection,
				...(((isWoocommerceEnabled || isShopifyEnabled) && !isEcomAnalyticsConversionEnabled) || isAgent
					? {}
					: { Commerce: DIVIDER }),
				'Agent Conversions': '/analytics/livechat/agent-conversions',
				'Purchase Funnel': '/analytics/livechat/purchase-funnel',
				'Product Analytics': '/analytics/livechat/product-analytics',
				'New Agent Performance': '/analytics/livechat/new-agent-performance',
				'Conversation Overview': '/analytics/livechat/conversation-overview',
				'Agent Assistance Feedback': '/analytics/livechat/agent-assistance-feedback',
			},
		],

		integrations: [
			{
				'Cloud Integrations': '/integrations/cloud-integrations',
				...(isAdminMode || !isAcceleratorEcom ? { 'BrandName API': '/integrations/platform-api' } : {}),
				'C2E Bridge': '/integrations/c2e-bridge',
				'API Logs': '/integrations/api-logs',
				'Agent Logs': '/integrations/agent-logs',
				'Channel Logs': '/integrations/channel-logs',
				'Payment Logs': '/integrations/payment-logs',
			},
		],
		whatsapp: [
			{
				Numbers: '/whatsApp/numbers',
				Flows: '/whatsApp/flows',
			},
		],
		accountSettings: [
			{
				'Profile Settings': '/account-settings/profile',
				'Region Settings': '/account-settings/region',
				'Theme Settings': '/account-settings/theme',
				'Account Details': '/account-settings/account',
				'Usage Details': '/account-settings/usage-details',
				[t('common:locations.navigation.usageBreakdown')]: '/account-settings/usage-breakdown',
				'Team Members': '/account-settings/team-members',
				'Billing Details': '/account-settings/billing',
				[t('common:locations.navigation.securitySettings')]: '/account-settings/security',
				'Notification Settings': '/account-settings/notification',
				[t('common:locations.navigation.passwordSettings')]: '/account-settings/password',
				...(isAdminMode
					? { [t('common:locations.navigation.whatsappSettings')]: '/account-settings/whatsapp-settings' }
					: {}),
			},
		],
		broadcast: [
			{
				...(marketingCampaignsAnalyticsType
					? {
							[t('common:locations.navigation.analytics')]: DIVIDER,
							[t('common:locations.navigation.execution')]: '/broadcast/execution',
							[t('common:locations.navigation.engagement')]: '/broadcast/engagement',
							...(isCDPEntitlementEnabled
								? {
										[t('common:locations.navigation.pulseReport')]: '/broadcast/report',
									}
								: {}),
							[t('common:setup')]: DIVIDER,
						}
					: { Campaigns: '/broadcast/campaigns', 'Broadcast History': '/broadcast/published' }),
				...(!marketingCampaignsAnalyticsType && {
					'Click to WhatsApp Ads': '/broadcast/clickToWhatsappAds',
				}),
				[t('common:locations.navigation.messageTemplates')]: '/broadcast/templates',
				[t('common:locations.navigation.rcsTemplates')]: '/broadcast/rcs-templates',
				'Email Templates': '/broadcast/email-templates',
				'Scheduled Broadcasts': '/broadcast/scheduled',
				'Draft Broadcasts': '/broadcast/draft',
				...(isCDPEntitlementEnabled ||
				marketingCampaignsAnalyticsType === MARKETING_CAMPAIGNS_ENTITLEMENT_VALUES.CONVERSION_ENABLED
					? {
							[t('common:locations.navigation.pulse')]: '/broadcast/pulse',
						}
					: {}),
				...(marketingCampaignsAnalyticsType ? { [t('common:locations.navigation.general')]: DIVIDER } : {}),
				'Export History': '/broadcast/exportHistory',
				Help: '/broadcast/help',
			},
		],
		store: [
			{
				Segments: '/store/segments',
				Products: '/store/products',
				'Conversion Triggers': '/store/conversion',
				'Feature Configurator': '/store/feature-configurator',
				Setup: '/store/setup',
			},
		],
		agents: [
			{
				Agents: DIVIDER,
				Build: '/agents/build',
				VoiceLibrary: '/agents/voices',
				Workflows: '/agents/workflows',
				Manage: DIVIDER,
				Route: '/agents/route',
				Analyze: DIVIDER,
				Dashboard: '/agents/performance',
			},
		],
	};

	const analyticsRoutes = [
		NavOptions.analytics[0][AnalyticsRoutekeys.AGENT_CONVERSIONS],
		NavOptions.analytics[0][AnalyticsRoutekeys.PURCHASE_FUNNEL],
		NavOptions.analytics[0][AnalyticsRoutekeys.PRODUCT_ANALYTICS],
	];

	const hasAccessFor = (route: string) => {
		if (route === '/analytics/livechat/agent-performance') {
			return leftPanelsV2.dashboard.agentPerformance || false;
		}
		if (route === '/integrations/payment-logs') {
			return isWhatsAppConfigured && isWaPaymentEnabled;
		}
		if (route === '/analytics/livechat/new-agent-performance') {
			return leftPanelsV2.enable_livechat_analytics_v2 || false;
		}
		if (route === purchaseFunnelLink && isAcceleratorEcom) {
			return false;
		}
		if (route === productAnalyticsLink && isAcceleratorEcom) {
			return false;
		}
		if (
			route === ThreeColumnRouteLinks.broadcastMessageTemplates &&
			!(isDialog360Configured || isWhatsAppCloudConfigured)
		) {
			return false;
		}
		if (route === ThreeColumnRouteLinks.broadcastRcsTemplates && !isRcsConfigured) {
			return false;
		}

		if (route === NavOptions.store[0].Segments && (!isSystemSegmentsEnabled || isAgent)) {
			return false;
		}
		if (route === NavOptions.store[0].Products && !shopifyProductDiscoveryEntitlement) {
			return false;
		}
		if (route === NavOptions.store[0]['Conversion Triggers'] && !engagementTriggersEnabled) {
			return false;
		}
		if (analyticsRoutes.includes(route)) {
			if (((isWoocommerceEnabled || isShopifyEnabled) && !isEcomAnalyticsConversionEnabled) || isAgent) {
				return false;
			}
		}
		if (route === storeSetupLink && isAcceleratorEcom && !isMasterAdmin) {
			return false;
		}
		if (route === storeCustomersLink && isAcceleratorEcom) {
			return false;
		}
		if (route === storeProductsLink && isAcceleratorEcom) {
			return false;
		}
		if (route === storeConversionTriggersLink && isAcceleratorEcom) {
			return false;
		}
		if (route === agentLogsLink) {
			return true;
		}
		if (route === broadcastEmailTemplates) {
			return isEmailTemplatesEnabled;
		}

		return leftPanelsV2Data.indexOf(LEFT_PANEL_ACCESS_ROUTES[route]) > -1 || LEFT_PANEL_ACCESS_ROUTES[route] === '';
	};

	const getLabel = (item: string) => {
		if (item === 'New Agent Performance') {
			if (!leftPanelsV2.dashboard.agentPerformance && leftPanelsV2.enable_livechat_analytics_v2) {
				return 'Agent Performance';
			}
		}
		if (item.indexOf('BrandName') > -1) {
			return brandName ? item.replace('BrandName', brandName) : item.replace('BrandName', '');
		}
		if (item === 'Documents') {
			return isLLMEnabled
				? t('common:locations.navigation.generativeAI')
				: t('common:locations.navigation.documents');
		}

		return item;
	};

	const renderNavItem = (item: string, route: string, nested = false) =>
		hasAccessFor(route) && (
			<NavItem
				key={item}
				label={getLabel(item)}
				to={route}
				isSelected={pathInUrl.indexOf(route) > -1}
				className={css({
					marginBottom: '-0.125rem',
					paddingTop: '0.5rem',
					paddingLeft: nested ? '2rem' : undefined,
				})}
				dataQa={item}
			/>
		);

	const renderAccordionSection = (section: IAccordionNavSection) => {
		const visibleItems = Object.keys(section.items).filter((item) => hasAccessFor(section.items[item]));

		if (!visibleItems.length) {
			return null;
		}

		const isSectionActive = visibleItems.some((item) => pathInUrl.indexOf(section.items[item]) > -1);
		const isSectionExpanded = expandedSections[section.id] ?? isSectionActive;

		return (
			<AccordionNavGroup
				key={section.id}
				id={section.id}
				title={section.title}
				isExpanded={isSectionExpanded}
				onChange={({ expanded }: any) =>
					setExpandedSections((prevState) => ({
						...prevState,
						[section.id]: expanded.includes(section.id),
					}))
				}
			>
				{visibleItems.map((item) => (
					<div key={item} className={css({ paddingLeft: '2rem' })}>
						{renderNavItem(item, section.items[item], true)}
					</div>
				))}
			</AccordionNavGroup>
		);
	};

	const hasAccessToAnyOfTheMenuItems = (options: any) =>
		Object.keys(options).some((key) =>
			isAccordionNavSection(options[key])
				? Object.values(options[key].items).some((route) => hasAccessFor(route as string))
				: hasAccessFor(options[key])
		);

	useLocationEffect((location: Location) => setCurrentPath(location.pathname));

	return NavOptions[page] ? (
		NavOptions[page].map((options: any, index: number) => (
			<Block key={`secondary-nav-${index}`}>
				{index && hasAccessToAnyOfTheMenuItems(options) ? (
					<AdvancedHorizontalDivider item={t('common:advanced')} />
				) : null}
				{Object.keys(options).map((item: string) => {
					const option = options[item];

					return option === DIVIDER ? (
						<AdvancedHorizontalDivider item={item} />
					) : isAccordionNavSection(option) ? (
						<Fragment key={item}>{renderAccordionSection(option)}</Fragment>
					) : (
						<Fragment key={item}>{renderNavItem(item, option)}</Fragment>
					);
				})}
			</Block>
		))
	) : (
		<></>
	);
};

export default ShopifyNavigation;
